import { MEDIA_STALE_TIME } from '@/utils/media';
import { MediaPackType, MediaProgramWithGuides } from '@/utils/media/types';
import { useCircle } from '@rhg/hooks/use-circle';
import createMediaV2Api from '@rhg/libs/api/create-media-v2-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    checkQueryCache,
    invalidateQueryKey,
    ItemToQueryMapping,
    queryClient,
    queryKeys,
    useQuery,
} from '@rhg/libs/react-query';
import { getRhgAppHeader } from '@rhg/utils/browser';
import { useMemo } from 'react';
import { useActiveCircleId } from '../use-active-circle';
import { cacheGuides } from '../use-guides';
import { programMetadataQueryOptions } from '../use-pack-guide-program-metadata';
import { invalidatePacks } from '../use-packs';

export const programsQueryOptions = {
    /**
     * The key should be a program ID map where each program ID maps to its corresponding program ID for easy invalidation
     * This allows us to invalidate any query with a given program ID by passing in [queryKeys.programs, circleId, { someProgramId: someProgramId }]
     *
     * Refs:
     * https://tanstack.com/query/latest/docs/framework/react/guides/query-invalidation#query-matching-with-invalidatequeries
     * https://tanstack.com/query/latest/docs/framework/react/guides/query-keys#array-keys-with-variables
     */
    queryKey: ({
        programIds,
        circleId,
    }: {
        programIds: Set<string>;
        circleId: number;
    }) => {
        return [
            queryKeys.programs,
            circleId,
            Array.from(programIds).reduce(
                (prev, programId) => ({
                    ...prev,
                    [programId]: programId,
                }),
                {} as Record<string, string>
            ),
        ];
    },
    queryFn: async ({
        getAccessTokenSilently,
        programIds,
        circleId,
        circleAuthorizationToken,
        rHGApp,
    }: {
        getAccessTokenSilently: () => Promise<string>;
        programIds: Set<string>;
        circleId: number;
        circleAuthorizationToken: string;
        rHGApp: string;
    }) => {
        const mediaV2Api = createMediaV2Api(getAccessTokenSilently);

        const response = await mediaV2Api.v2GetPrograms({
            circleAuthorizationToken,
            programId: Array.from(programIds),
            recipientId: circleId,
            rHGApp,
        });

        return response.data.programs as MediaProgramWithGuides[];
    },
    queryOptions: ({
        programIds,
        isAuthenticated,
        circleId,
        circleAuthorizationToken,
    }: {
        programIds: Set<string>;
        isAuthenticated: boolean;
        circleId: number;
        circleAuthorizationToken: string;
    }) => {
        return {
            enabled: !!(
                isAuthenticated &&
                circleId > 0 &&
                circleAuthorizationToken &&
                programIds.size > 0
            ),
            staleTime: MEDIA_STALE_TIME,
        };
    },
};

export function cachePrograms(
    programs: MediaProgramWithGuides[] | undefined,
    circleId: number,
    _queryClient = queryClient
) {
    (programs ?? []).forEach((program) => {
        const key = programsQueryOptions.queryKey({
            programIds: new Set([program.programId]),
            circleId,
        });
        _queryClient.setQueryData<MediaProgramWithGuides[]>(key, [program]);

        cacheGuides(program.guides, circleId, _queryClient);
    });
}

export async function invalidatePrograms({
    programIds,
    circleId,
}: {
    programIds: Set<string>;
    circleId: number;
}) {
    if (!programIds.size) {
        return;
    }

    await Promise.all(
        Array.from(programIds).flatMap((programId) => [
            invalidateQueryKey(
                programsQueryOptions.queryKey({
                    programIds: new Set([programId]),
                    circleId: circleId,
                })
            ),
            invalidateQueryKey(
                programMetadataQueryOptions.queryKey({
                    programIds: new Set([programId]),
                    circleId: circleId,
                })
            ),
        ])
    );

    // Invalidate packs that are associated with the programs
    const relatedPackQueries = queryClient.getQueriesData<MediaPackType[]>({
        queryKey: [queryKeys.packsByPackClass, circleId],
        predicate: (query) => {
            if (!query.queryKey) {
                return false;
            }

            // Ignore batch query keys -- we'll invalidate their pieces
            const packClassObject = query.queryKey[2] as Record<string, string>;
            const packClasses =
                packClassObject && typeof packClassObject === 'object'
                    ? Object.keys(packClassObject)
                    : [];
            if (packClasses.length !== 1) {
                return false;
            }

            // Ignore already-invalidated queries
            const queryState = query.state;
            const isValid =
                queryState &&
                !queryState.isInvalidated &&
                queryState.dataUpdatedAt &&
                queryState.dataUpdatedAt > Date.now() - MEDIA_STALE_TIME;
            if (!isValid) {
                return false;
            }

            // Find all packs that contain the guides
            const packs = queryState.data;
            const pack = packs && Array.isArray(packs) ? packs[0] : undefined;
            if (!pack) {
                return false;
            }
            const programIsInPack = pack.items?.some((item) => {
                return (
                    item.type === 'program' && programIds.has(item.programId)
                );
            });
            return programIsInPack;
        },
    });
    const relatedPackClasses = relatedPackQueries.reduce<string[]>(
        (acc, [, packs]) => {
            const packClasses = packs?.map((pack) => pack.packClass) ?? [];
            return acc.concat(packClasses);
        },
        []
    );
    invalidatePacks({ packClasses: relatedPackClasses, circleId });
}

export function usePrograms(_programIds: string[], _circleId?: number) {
    const { activeCircleId } = useActiveCircleId();
    const circleId = _circleId || activeCircleId;
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { circle } = useCircle(circleId);
    const circleAuthorizationToken = circle?.authorizationJWT ?? '';
    const { queryKey, queryFn, queryOptions } = programsQueryOptions;
    const rHGApp = getRhgAppHeader();
    const programIds = useMemo(() => new Set(_programIds), [_programIds]);

    const options = useMemo(
        () =>
            queryOptions({
                programIds,
                isAuthenticated,
                circleId,
                circleAuthorizationToken,
            }),
        [
            circleAuthorizationToken,
            circleId,
            programIds,
            isAuthenticated,
            queryOptions,
        ]
    );

    const { data, ...otherProps } = useQuery({
        queryKey: queryKey({
            programIds,
            circleId,
        }),
        queryFn: async () => {
            if (!programIds.size) return [];
            const _programIdsArray = Array.from(programIds);

            const keyObjects = _programIdsArray.map(
                (programId): ItemToQueryMapping<string> => ({
                    item: programId,
                    queryKey: queryKey({
                        programIds: new Set([programId]),
                        circleId,
                    }),
                })
            );

            const { cachedQueries, invalidQueries } = checkQueryCache<
                string,
                MediaProgramWithGuides[]
            >(keyObjects, options.staleTime);
            const cachedQueriesData = cachedQueries.reduce<
                MediaProgramWithGuides[]
            >((cachedData, item) => [...cachedData, ...item.data], []);

            // If all programs are already fetched and not stale, return them
            if (invalidQueries.length === 0) {
                return cachedQueriesData;
            }

            const programsIdsToFetch = invalidQueries.map(({ item }) => item);
            const response = await queryFn({
                getAccessTokenSilently,
                programIds: new Set(programsIdsToFetch),
                circleId,
                circleAuthorizationToken,
                rHGApp,
            });

            // populate cache for each program
            cachePrograms(response, circleId);

            // Combine newly fetched packs with existing packs
            return [...cachedQueriesData, ...(response || [])];
        },
        ...options,
    });

    return {
        programs: data,
        isEnabled: options.enabled,
        ...otherProps,
    };
}
